/**
 * StatusBadge Component
 *
 * Reusable status badge with predefined color mappings.
 * Supports custom status maps and handles null/unknown states gracefully.
 *
 * @example
 * ```tsx
 * // Simple usage with default status map
 * <StatusBadge status="APPOINTMENT_COMPLETED" />
 *
 * // With custom status map
 * <StatusBadge
 *   status={consult.status}
 *   statusMap={customStatusOptions}
 * />
 *
 * // Null handling
 * <StatusBadge status={null} /> // Renders "-"
 *
 * // Unknown status handling
 * <StatusBadge status="UNKNOWN_STATUS" /> // Renders as outline badge
 * ```
 */

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { BADGE_SIZE_CLASSES } from "@/utils/badge";
import type { BadgeSize } from "@/utils/badge";

/**
 * Status option configuration
 */
export interface StatusOption {
  /** Status value (e.g., "APPOINTMENT_COMPLETED") */
  value: string;
  /** Display label (e.g., "예약완료") */
  label: string;
  /** Tailwind CSS color classes */
  color: string;
}

/**
 * Default status options for consultation system
 * Matches backend Consult.ConsultStatus enum
 */
export const DEFAULT_STATUS_OPTIONS: StatusOption[] = [
  {
    value: "PENDING",
    label: "대기",
    color: "bg-muted text-muted-foreground hover:bg-muted/80 transition-colors",
  },
  {
    value: "FAILED",
    label: "실패",
    color: "bg-destructive hover:bg-destructive/90 text-destructive-foreground transition-colors",
  },
  {
    value: "IN_PROGRESS",
    label: "처리 중",
    color: "bg-info hover:bg-info/90 text-info-foreground transition-colors",
  },
  {
    value: "CALLBACK_NEEDED",
    label: "콜백 필요",
    color: "bg-warning hover:bg-warning/90 text-warning-foreground transition-colors",
  },
  {
    value: "CALLBACK_COMPLETED",
    label: "콜백 완료",
    color: "bg-purple-500 hover:bg-purple-600 text-white transition-colors",
  },
  {
    value: "OUTBOUND_NEEDED",
    label: "아웃바운드 필요",
    color: "bg-destructive hover:bg-destructive/90 text-destructive-foreground transition-colors",
  },
  {
    value: "OUTBOUND_COMPLETED",
    label: "아웃바운드 완료",
    color: "bg-purple-700 hover:bg-purple-800 text-white transition-colors",
  },
  {
    value: "COMPLETED",
    label: "처리 완료",
    color: "bg-primary hover:bg-primary/90 text-primary-foreground transition-colors",
  },
];

/**
 * H-3: 상태 전이 규칙 (백엔드 ConsultStatus.ALLOWED_TRANSITIONS 미러)
 */
export const ALLOWED_TRANSITIONS: Record<string, string[]> = {
  PENDING: [
    "IN_PROGRESS",
    "CALLBACK_NEEDED",
    "OUTBOUND_NEEDED",
    "COMPLETED",
    "FAILED",
  ],
  IN_PROGRESS: [
    "CALLBACK_NEEDED",
    "OUTBOUND_NEEDED",
    "COMPLETED",
    "FAILED",
  ],
  CALLBACK_NEEDED: ["CALLBACK_COMPLETED", "IN_PROGRESS", "COMPLETED"],
  CALLBACK_COMPLETED: ["IN_PROGRESS", "COMPLETED"],
  OUTBOUND_NEEDED: ["OUTBOUND_COMPLETED", "IN_PROGRESS", "COMPLETED"],
  OUTBOUND_COMPLETED: ["IN_PROGRESS", "COMPLETED"],
  COMPLETED: ["IN_PROGRESS", "CALLBACK_NEEDED"],
  FAILED: ["PENDING", "IN_PROGRESS"],
};

/**
 * 현재 상태에서 전이 가능한 상태 옵션 반환
 */
export function getAvailableStatusOptions(
  currentStatus: string,
): StatusOption[] {
  const allowed = ALLOWED_TRANSITIONS[currentStatus] || [];
  const statusMap = new Map(
    DEFAULT_STATUS_OPTIONS.map((opt) => [opt.value, opt]),
  );
  const current = statusMap.get(currentStatus);
  const options = allowed
    .map((key) => statusMap.get(key))
    .filter(Boolean) as StatusOption[];
  return current ? [current, ...options] : options;
}

/**
 * StatusBadge component props
 */
export interface StatusBadgeProps {
  /** Status value to display */
  status?: string | null;
  /** Custom status options map (defaults to DEFAULT_STATUS_OPTIONS) */
  statusMap?: StatusOption[];
  /** Badge variant (defaults to "outline") */
  variant?: "default" | "outline" | "secondary" | "destructive";
  /** Badge size */
  size?: BadgeSize;
  /** Additional className */
  className?: string;
  /** Text to show when status is null/undefined */
  emptyText?: string;
}

/**
 * StatusBadge Component
 *
 * Features:
 * - Predefined status color mappings
 * - Custom status map support
 * - Null/undefined handling
 * - Unknown status fallback
 * - Configurable size and variant
 *
 * @param props - StatusBadge configuration
 * @returns Rendered status badge
 */
export function StatusBadge({
  status,
  statusMap = DEFAULT_STATUS_OPTIONS,
  variant = "outline",
  size = "sm",
  className,
  emptyText = "-",
}: StatusBadgeProps) {
  /**
   * Handle null or undefined status
   */
  if (!status) {
    return <span className="text-muted-foreground text-xs">{emptyText}</span>;
  }

  /**
   * Find matching status option
   */
  const statusOption = statusMap.find((opt) => opt.value === status);

  /**
   * Handle unknown status (not in map)
   */
  if (!statusOption) {
    return (
      <Badge variant={variant} className={cn("text-xs", className)}>
        {status}
      </Badge>
    );
  }

  /**
   * Render badge with status option colors
   */
  return (
    <Badge
      variant={variant}
      className={cn(BADGE_SIZE_CLASSES[size], statusOption.color, className)}
    >
      {statusOption.label}
    </Badge>
  );
}

/**
 * Task #39: 예약(Appointment) 상태 배지 공통 컴포넌트
 *
 * Consult.AppointmentStatus 기반 상태 배지 — 중복 inline getStatusBadge 대체.
 * REQUESTED/CHECKING → "신청" (묶음 처리, 오렌지)
 * CONFIRMED           → "확정" (초록)
 * CANCELLED           → "취소" (빨강)
 * NO_SHOW             → "노쇼" (회색 진한)
 * COMPLETED           → "완료" (회색)
 * SMS_SENT            → "안내" (보라)
 * AVAILABLE           → "가능" (하늘)
 * NOT_AVAILABLE       → "불가" (주황)
 */

interface AppointmentStatusConfig {
  label: string;
  className: string;
}

const APPOINTMENT_STATUS_CONFIG: Record<string, AppointmentStatusConfig> = {
  REQUESTED: {
    label: "신청",
    className:
      "bg-warning/15 text-warning hover:bg-warning/15 border-warning/30",
  },
  CHECKING: {
    label: "신청",
    className:
      "bg-warning/15 text-warning hover:bg-warning/15 border-warning/30",
  },
  CONFIRMED: {
    label: "확정",
    className:
      "bg-success/15 text-success hover:bg-success/15 border-success/30",
  },
  CANCELLED: {
    label: "취소",
    className: "bg-destructive/10 text-destructive hover:bg-destructive/10 border-destructive/20",
  },
  NO_SHOW: {
    label: "노쇼",
    className: "bg-foreground/80 text-background hover:bg-foreground/80",
  },
  COMPLETED: {
    label: "완료",
    className: "bg-info/15 text-info hover:bg-info/15 border-info/30",
  },
  SMS_SENT: {
    label: "안내",
    className:
      "bg-purple-100 text-purple-700 hover:bg-purple-100 border-purple-200",
  },
  AVAILABLE: {
    label: "가능",
    className: "bg-sky-100 text-sky-700 hover:bg-sky-100 border-sky-200",
  },
  NOT_AVAILABLE: {
    label: "불가",
    className:
      "bg-warning/15 text-warning hover:bg-warning/15 border-warning/30",
  },
  // Phase 0C: 신규 상태
  MODIFICATION_REQUESTED: {
    label: "변경요청",
    className:
      "bg-amber-100 text-amber-700 hover:bg-amber-100 border-amber-200",
  },
  CANCELLATION_REQUESTED: {
    label: "취소요청",
    className:
      "bg-rose-100 text-rose-700 hover:bg-rose-100 border-rose-200",
  },
  PENDING: {
    label: "대기",
    className:
      "bg-muted text-muted-foreground hover:bg-muted border-border",
  },
};

interface AppointmentStatusBadgeProps {
  status?: string | null;
  className?: string;
}

/**
 * 예약 상태 배지 공통 컴포넌트 (Task #39)
 *
 * @example
 * <AppointmentStatusBadge status={consult.appointmentStatus} />
 */
export function AppointmentStatusBadge({
  status,
  className,
}: AppointmentStatusBadgeProps) {
  if (!status) {
    return (
      <Badge variant="outline" className={cn("text-[10px] h-5", className)}>
        -
      </Badge>
    );
  }

  const config = APPOINTMENT_STATUS_CONFIG[status];
  if (!config) {
    return (
      <Badge variant="outline" className={cn("text-[10px] h-5", className)}>
        {status}
      </Badge>
    );
  }

  return (
    <Badge
      className={cn("text-[10px] h-5", config.className, className)}
    >
      {config.label}
    </Badge>
  );
}

/**
 * Export default
 */
export default StatusBadge;
